/**
 * TemplatePreviewNotice Component
 *
 * Displays an informational notice when viewing a survey template in preview mode.
 * Informs users they need to create a copy to edit the template.
 *
 * ## Features
 * - **Accessibility**: Includes ARIA attributes (role="status", aria-live="polite")
 * - **Visual Design**: Light error/info background (5% opacity) matching application patterns
 * - **Animation**: Smooth 300ms slide-down entrance effect
 * - **Always Visible**: Unlike UnsavedChangesWarning, this always renders in preview mode
 *
 * ## Positioning
 * Placed in the same location as UnsavedChangesWarning:
 * - Between SurveyActions header and TabRoot component
 * - Visible immediately after the header
 * - Above the content being previewed
 *
 * ## User Experience
 * - Appears when viewing template in preview mode
 * - Clarifies that this is a read-only preview
 * - Instructs user to create a copy to make edits
 * - Does not interrupt user workflow (polite announcement)
 */

import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { addAlphaToHex } from '../../../../../../models/utils/colorUtils';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { LightBulbIcon } from '../../../../../components/svgs/icons/LightBulbIcon';
import { brandPrimary } from '../../../../../styles/colors';

/**
 * Props for TemplatePreviewNotice component
 */
export interface TemplatePreviewNoticeProps {
	/**
	 * Optional additional className for styling overrides
	 */
	className?: string;

	/**
	 * Test ID for automated testing
	 *
	 * @default "template-preview-notice"
	 */
	testId?: string;
}

/**
 * Notice component to inform users they are viewing a template preview
 *
 * @example Basic usage
 * ```tsx
 * <TemplatePreviewNotice />
 * ```
 *
 * @example With custom styling
 * ```tsx
 * <TemplatePreviewNotice className={css(customStyles.extraMargin)} />
 * ```
 */
export const TemplatePreviewNotice: React.FC<TemplatePreviewNoticeProps> = ({
	className,
	testId = 'template-preview-notice',
}) => {
	return (
		<div
			// ARIA: Identifies this as a status message for screen readers
			role='status'
			// ARIA: Polite announcement - won't interrupt current screen reader activity
			aria-live='polite'
			// Apply both default styles and optional custom className
			className={className ? `${css(styles.noticeMessage)} ${className}` : css(styles.noticeMessage)}
			// Test ID for automated testing
			data-testid={testId}
		>
			<span className={css(styles.messageContent)}>
				<LightBulbIcon width={16} height={16} fill={brandPrimary} styles={[styles.icon]} />
				You are viewing a preview of this template. To make edits, please create a copy using the &ldquo;Duplicate
				Survey&rdquo; button.
			</span>
		</div>
	);
};

/**
 * Styles for TemplatePreviewNotice
 *
 * Uses error color at 5% opacity for a subtle informational background
 */
const styles = StyleSheet.create({
	noticeMessage: {
		// Visual Design - 5% opacity error color background
		background: addAlphaToHex(brandPrimary, 0.05), // #B34755 at 5% opacity over white
		fontSize: 14,
		textAlign: 'center',
		padding: 11,

		// Layout
		position: 'relative',
		zIndex: 1,
		marginBottom: 20,

		// Animation: Smooth slide-down entrance effect
		animationName: {
			from: {
				opacity: 0,
				transform: 'translateY(-10px)',
			},
			to: {
				opacity: 1,
				transform: 'translateY(0)',
			},
		},
		animationDuration: '300ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'both',
	},
	messageContent: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	icon: {
		flexShrink: 0,
	},
});
